import React, { useEffect, useState } from "react";
import { getAthleteSummary } from "../../api/athleteMonitoringAPI";
import ProficiencyIndicator from "./common/ProficiencyIndicator";
import { useTheme } from "../../context/ThemeContext";
import { Card, SectionHeading } from "../ui";
import {
  getPrimaryText,
  getMutedText,
  getLightBorder,
} from "../../utils/themeUtils";
import { BarChart3, Target, Trophy } from "lucide-react";

interface Athlete {
  id: string;
  first_name?: string;
  last_name?: string;
  name?: string;
}

interface DashboardProgressWidgetProps {
  athletes: Athlete[];
}

interface AthleteSummaryData {
  athleteId: string;
  athleteName: string;
  stats: {
    totalSkills: number;
    assessedSkills: number;
    avgProficiency: number;
    activeGoals: number;
    totalMilestones: number;
  };
  latestMilestone?: string;
}

const DashboardProgressWidget: React.FC<DashboardProgressWidgetProps> = ({ athletes }) => {
  const { isDarkMode } = useTheme();
  const token = localStorage.getItem("authToken");

  const [summaries, setSummaries] = useState<AthleteSummaryData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummaries();
  }, [athletes]);

  const fetchSummaries = async () => {
    if (!token || !athletes || athletes.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const results: AthleteSummaryData[] = [];
    for (const athlete of athletes) {
      const res = await getAthleteSummary(token, athlete.id);
      if (res?.success && res.data.stats.assessedSkills > 0) {
        const athleteName = athlete.first_name
          ? `${athlete.first_name} ${athlete.last_name || ""}`
          : athlete.name || "Athlete";
        results.push({
          athleteId: athlete.id,
          athleteName: athleteName.trim(),
          stats: res.data.stats,
          latestMilestone: res.data.milestones?.[0]?.title,
        });
      }
    }

    setSummaries(results);
    setLoading(false);
  };

  if (loading) {
    return (
      <Card padding="md" className="animate-pulse" role="status" aria-label="Loading">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3" />
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded" />
      </Card>
    );
  }

  if (summaries.length === 0) return null;

  return (
    <Card padding="md">
      <SectionHeading>Athlete Progress</SectionHeading>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {summaries.map((s) => (
          <div
            key={s.athleteId}
            className={`p-3 rounded-lg border ${getLightBorder(isDarkMode)}`}
          >
            <p className={`text-sm font-medium ${getPrimaryText(isDarkMode)} mb-2`}>
              {s.athleteName}
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4 text-blue-500" />
                <span className={`text-xs ${getMutedText(isDarkMode)}`}>
                  {s.stats.assessedSkills}/{s.stats.totalSkills} skills
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Target className="w-4 h-4 text-green-500" />
                <span className={`text-xs ${getMutedText(isDarkMode)}`}>
                  {s.stats.activeGoals} goal{s.stats.activeGoals !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Trophy className="w-4 h-4 text-yellow-500" />
                <span className={`text-xs ${getMutedText(isDarkMode)}`}>
                  {s.stats.totalMilestones} milestone{s.stats.totalMilestones !== 1 ? "s" : ""}
                </span>
              </div>
              {s.stats.avgProficiency > 0 && (
                <ProficiencyIndicator
                  proficiency={Math.round(s.stats.avgProficiency)}
                  max={5}
                  size="sm"
                  showLabel
                />
              )}
            </div>
            {s.latestMilestone && (
              <p className={`text-xs ${getMutedText(isDarkMode)} mt-2 italic`}>
                Latest: {s.latestMilestone}
              </p>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default DashboardProgressWidget;
